import { useMemo, useState } from "react"
import { Link } from "react-router-dom"
import { BoltIcon, ExclamationTriangleIcon } from "@heroicons/react/24/outline"
import { Layout } from "../components/Layout"
import { TableSkeleton } from "@/components/Skeleton/Skeleton"
import { ErrorFallback } from "@/components/ErrorBoundary/ErrorBoundary"
import { PROCESSING_STAGES, STAGE_STATUSES, type StageStatus } from "@/hooks/useClips"
import {
  useProcessingEvents,
  type ProcessingEvent,
  type ProcessingStageFilter,
  type ProcessingStatusFilter,
} from "@/hooks/useProcessingEvents"

const STAGE_STATUS_STYLES: Record<StageStatus, string> = {
  pending: "bg-gray-100 text-gray-700",
  running: "bg-blue-100 text-blue-800",
  succeeded: "bg-green-100 text-green-800",
  failed: "bg-red-100 text-red-800",
  skipped: "bg-transparent text-gray-400 border border-dashed border-gray-300",
}

function StageBadge({ status }: { status: StageStatus }) {
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${STAGE_STATUS_STYLES[status]}`}
    >
      {status}
    </span>
  )
}

// D1 stores timestamps as "YYYY-MM-DD HH:MM:SS" (UTC, no zone marker). Normalize to ISO-UTC
// so new Date() does not read them as local time. (Same helper as the Clips page.)
function parseUtc(value: string | null): number {
  if (!value) return NaN
  const iso = /[T].*(Z|[+-]\d\d:?\d\d)$/.test(value) ? value : value.replace(" ", "T") + "Z"
  return new Date(iso).getTime()
}

function formatAge(value: string | null): string {
  const timestamp = parseUtc(value)
  if (Number.isNaN(timestamp)) return "—"
  const seconds = Math.max(0, Math.floor((Date.now() - timestamp) / 1000))
  if (seconds < 10) return "now"
  if (seconds < 60) return `${seconds}s ago`
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  return `${days}d ago`
}

function formatAbsolute(value: string | null): string {
  const timestamp = parseUtc(value)
  if (Number.isNaN(timestamp)) return "—"
  return new Date(timestamp).toLocaleString()
}

function StatCard({ label, value }: { label: string; value: number | string }) {
  return (
    <div className="bg-surface rounded-lg border border-border p-4">
      <div className="text-secondary text-sm mb-1">{label}</div>
      <div className="text-2xl font-semibold text-primary">{value}</div>
    </div>
  )
}

function ToggleFilter<T extends string>({
  options,
  value,
  onChange,
  labelFor,
}: {
  options: readonly T[]
  value: T
  onChange: (value: T) => void
  labelFor: (value: T) => string
}) {
  return (
    <div className="inline-flex rounded-md border border-border bg-background p-1">
      {options.map((option) => (
        <button
          key={option}
          type="button"
          onClick={() => onChange(option)}
          className={`h-8 min-w-[72px] rounded px-3 text-sm font-medium transition-colors ${
            value === option ? "bg-primary text-contrast" : "text-secondary hover:text-primary"
          }`}
        >
          {labelFor(option)}
        </button>
      ))}
    </div>
  )
}

function LoadMoreButton({ onClick, loading }: { onClick: () => void; loading: boolean }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={loading}
      className="rounded-md border border-border bg-surface px-3 py-1.5 text-sm text-primary hover:bg-background disabled:opacity-50"
    >
      {loading ? "Loading…" : "Load more"}
    </button>
  )
}

export function RecentOperations() {
  const [stage, setStage] = useState<ProcessingStageFilter>("all")
  const [status, setStatus] = useState<ProcessingStatusFilter>("all")
  const [stuck, setStuck] = useState(false)

  const { data, isLoading, error, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useProcessingEvents({ stage, status, stuck })

  // Flatten the loaded pages and dedupe by a composite key. Offset pagination over a list
  // that grows at the top while we poll can repeat a row at a page boundary; the dedupe
  // keeps the rendered list clean.
  const events = useMemo(() => {
    const flat = data?.pages.flatMap((p) => p.events) ?? []
    const seen = new Set<string>()
    const out: ProcessingEvent[] = []
    for (const e of flat) {
      const key = `${e.clip_public_id}|${e.stage}|${e.attempt}|${e.created_at}|${e.status}`
      if (seen.has(key)) continue
      seen.add(key)
      out.push(e)
    }
    return out
  }, [data?.pages])

  const thresholdMinutes = data?.pages[0]?.stuckThresholdMinutes ?? 30
  // Read the server-computed flag (single source of truth) rather than re-deriving "old +
  // non-terminal", which would false-flag a row that has since been superseded.
  const stuckCount = useMemo(() => events.filter((e) => e.is_stuck).length, [events])

  if (error) {
    return (
      <Layout>
        <ErrorFallback error={error as Error} />
      </Layout>
    )
  }

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-primary">Recent Operations</h1>
            <p className="text-secondary">
              Task operations across all teams (transcode, cover, AI), newest activity first.
              Stuck = non-terminal for more than {thresholdMinutes} min.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => setStuck((v) => !v)}
              className={`inline-flex h-8 items-center gap-1.5 rounded-md border px-3 text-sm font-medium transition-colors ${
                stuck
                  ? "border-amber-300 bg-amber-100 text-amber-900"
                  : "border-border bg-background text-secondary hover:text-primary"
              }`}
            >
              <ExclamationTriangleIcon className="h-4 w-4" />
              Stuck
            </button>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value as ProcessingStatusFilter)}
              disabled={stuck}
              className="h-8 rounded-md border border-border bg-background px-2 text-sm text-primary disabled:opacity-50"
              aria-label="Filter by status"
            >
              <option value="all">All statuses</option>
              {STAGE_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
            <ToggleFilter
              options={["all", ...PROCESSING_STAGES] as const}
              value={stage}
              onChange={setStage}
              labelFor={(value) => (value === "all" ? "All" : value)}
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
          <StatCard label="Loaded" value={events.length} />
          <StatCard label={`Stuck (>${thresholdMinutes}m)`} value={stuckCount} />
          <StatCard label="View" value={stuck ? "Stuck only" : "All recent"} />
        </div>

        {isLoading ? (
          <TableSkeleton rows={10} />
        ) : events.length === 0 ? (
          <div className="flex items-center gap-2 rounded-lg border border-border bg-surface p-6 text-secondary">
            <BoltIcon className="h-5 w-5" />
            {stuck ? "No stuck operations." : "No operations match these filters."}
          </div>
        ) : (
          <div className="overflow-hidden rounded-lg border border-border bg-surface">
            <table className="w-full text-sm">
              <thead className="text-left text-xs uppercase tracking-wide text-secondary">
                <tr className="border-b border-border">
                  <th className="px-3 py-2">When</th>
                  <th className="px-3 py-2">Stage</th>
                  <th className="px-3 py-2">Status</th>
                  <th className="px-3 py-2">Attempt</th>
                  <th className="px-3 py-2">Trigger</th>
                  <th className="px-3 py-2">Clip</th>
                  <th className="px-3 py-2">Game</th>
                  <th className="px-3 py-2">Team</th>
                  <th className="px-3 py-2">Detail</th>
                </tr>
              </thead>
              <tbody>
                {events.map((event, i) => {
                  const stuckRow = event.is_stuck
                  return (
                    <tr
                      key={`${event.clip_public_id}-${event.stage}-${event.attempt}-${event.created_at}-${i}`}
                      className={`border-t border-border align-top ${
                        stuckRow ? "bg-amber-50" : ""
                      }`}
                    >
                      <td className="px-3 py-2 text-secondary" title={formatAbsolute(event.created_at)}>
                        {formatAge(event.created_at)}
                        {stuckRow && (
                          <span className="ml-1.5 inline-flex items-center rounded bg-amber-200 px-1.5 py-0.5 text-xs font-medium text-amber-900">
                            stuck
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-primary">{event.stage}</td>
                      <td className="px-3 py-2">
                        <StageBadge status={event.status} />
                      </td>
                      <td className="px-3 py-2 text-primary">{event.attempt}</td>
                      <td className="px-3 py-2 text-secondary">{event.trigger ?? "—"}</td>
                      <td className="px-3 py-2">
                        {event.game_public_id ? (
                          <Link
                            to={`/clips?game=${event.game_public_id}`}
                            className="font-mono text-xs text-primary hover:underline"
                          >
                            {event.clip_public_id}
                          </Link>
                        ) : (
                          <span className="font-mono text-xs text-primary">
                            {event.clip_public_id}
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        {event.game_public_id ? (
                          <Link
                            to={`/games/${event.game_public_id}`}
                            className="font-mono text-xs text-secondary hover:underline"
                          >
                            {event.game_public_id}
                          </Link>
                        ) : (
                          <span className="text-xs text-secondary">—</span>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        {event.team_public_id ? (
                          <Link
                            to={`/teams/${event.team_public_id}`}
                            className="text-xs text-secondary hover:underline"
                            title={event.team_public_id}
                          >
                            {event.team_name ?? event.team_public_id}
                          </Link>
                        ) : (
                          <span className="text-xs text-secondary">—</span>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        {event.error ? (
                          <span className="text-red-500" title={event.error}>
                            {event.error.slice(0, 80)}
                          </span>
                        ) : event.detail ? (
                          <span
                            className="text-secondary"
                            title={JSON.stringify(event.detail)}
                          >
                            {event.duration_ms != null ? `${event.duration_ms}ms · ` : ""}
                            {JSON.stringify(event.detail).slice(0, 60)}
                          </span>
                        ) : event.duration_ms != null ? (
                          <span className="text-secondary">{event.duration_ms}ms</span>
                        ) : (
                          <span className="text-secondary">—</span>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
            <div className="flex items-center justify-between gap-3 border-t border-border px-4 py-2.5 text-xs text-secondary">
              <span>
                {events.length} operation{events.length === 1 ? "" : "s"} loaded
              </span>
              {hasNextPage && (
                <LoadMoreButton onClick={() => fetchNextPage()} loading={isFetchingNextPage} />
              )}
            </div>
          </div>
        )}
      </div>
    </Layout>
  )
}
